import { useCallback, useEffect, useRef } from "react";

import type { ViewLayoutFragment } from "@/__generated__/graphql";
import type { CreateNodeContextType } from "@/app/app/_/commands";
import { useAppRouteNavigation, useAppRouteState } from "@/app/app/_/routing";
import { ReportsViewCanvas } from "@/app/app/_/views/ReportsView/ReportsViewCanvas";
import { useReportsViewReportLayoutContext } from "@/app/app/_/views/ReportsView/ReportsViewReportLayoutContext";
import { ReportsViewReportLayoutScope } from "@/app/app/_/views/ReportsView/ReportsViewReportLayoutScope";
import {
  getSelectedViewId,
  getReportsTabKey,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { DataView } from "@/components/organisms/DataView";
import { usePageThreadReference } from "@/components/organisms/Thread/Thread.hooks";
import { useConnectionSetup } from "@/hooks/useConnectionSetup";
import { useScope } from "@/hooks/useScope";
import { Objects } from "@/objects";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { State } from "@/state";

function ReportsViewContent() {
  const routeState = useAppRouteState();
  const { navigateToView } = useAppRouteNavigation();
  const activeTab = State((state) => state.activeTab);
  const activeTabNodeIds = State((state) => state.activeTabNodeIds);
  const setActiveTabNodeIds = State((state) => state.setActiveTabNodeIds);
  const setActivePanelNodeId = State((store) => store.setActivePanelNodeId);
  const setIsNodePanelOpen = State((store) => store.setIsNodePanelOpen);
  const {
    layouts,
    setActiveLayoutId,
    refetch: refetchLayouts,
  } = useReportsViewReportLayoutContext();
  const { entities, activeEntityIdentifiers } = useScope();

  const selectedViewId = getSelectedViewId(
    activeTabNodeIds,
    activeTab,
    routeState.viewId,
  );
  // Whether a report is open is the URL's to say, as it is for the page around
  // this view: the URL is what gives the page its padding, the recessed
  // surface behind the cards and the report's toolbars. A click reaches the
  // store a moment before it reaches the URL, and a canvas drawn on the
  // store's word stood for those frames in a page still dressed as the list -
  // flush to the edges, no toolbar - until the padding arrived around it.
  const isReportOpen =
    routeState.view === "reports" && Boolean(routeState.viewId);

  const {
    columns,
    rows,
    loading,
    totalCount,
    deleteNodeButtonProps,
    loadNext,
    networkStatus,
    refetch: refetchRows,
  } = useConnectionSetup<ViewLayoutFragment>({
    appView: "reports",
    skip: isReportOpen,
  });

  useEffect(() => {
    if (!selectedViewId) return;
    const layout =
      layouts.find((l) => l.id === selectedViewId) ??
      layouts.find((l) => l.identifier === selectedViewId);
    if (layout) {
      setActiveLayoutId(layout.identifier);
    }
  }, [selectedViewId, layouts, setActiveLayoutId]);

  // Opening a report points the node panel at it - the report's sources are
  // one click away in its toolbar - but leaves the panel shut, and shuts it if
  // it was open: the report takes the whole page. However the report is
  // opened (row click, new report, deep link, route hydration), this is where
  // it happens, once per report opened, so a panel the user opens afterwards
  // stays open.
  const openedReportNodeIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedViewId) {
      openedReportNodeIdRef.current = null;
      return;
    }

    const layout =
      layouts.find((l) => l.id === selectedViewId) ??
      layouts.find((l) => l.identifier === selectedViewId);
    const reportNodeId = layout?.id ?? selectedViewId;
    if (openedReportNodeIdRef.current === reportNodeId) return;
    openedReportNodeIdRef.current = reportNodeId;

    setActivePanelNodeId(reportNodeId, { autofocus: false });
    setIsNodePanelOpen(false);
  }, [layouts, selectedViewId, setActivePanelNodeId, setIsNodePanelOpen]);

  const handleRowClick = useCallback(
    (row: { id: string }) => {
      const tabKey = getReportsTabKey(activeTab);
      const layout = layouts.find((l) => l.id === row.id);
      if (layout) {
        setActiveLayoutId(layout.identifier);
      }
      setActiveTabNodeIds({
        ...State.getState().activeTabNodeIds,
        reports: {
          ...(((
            State.getState().activeTabNodeIds as {
              reports?: Record<string, string | null>;
            }
          )?.reports || {}) as Record<string, string | null>),
          [tabKey]: row.id,
        },
      });
      navigateToView("reports", {
        viewId: row.id,
        preservePanel: true,
        preserveQuery: true,
      });
    },
    [
      activeTab,
      layouts,
      navigateToView,
      setActiveLayoutId,
      setActiveTabNodeIds,
    ],
  );

  // Named, and of the businesses picked in the app until it is told otherwise,
  // in the dialog any record is made in; opened once it is made, as a card is.
  const handleAddNew = useCallback(() => {
    const context: CreateNodeContextType = {
      initialFieldValues: {
        entities: activeEntityIdentifiers.flatMap((identifier) =>
          (entities ?? []).filter((entity) => entity.identifier === identifier),
        ),
      },
      onCreated: async (created) => {
        // Neither list knows of the report the dialog made. The canvas draws
        // the first report for one it cannot find, so the report is in its
        // list before it is opened.
        void refetchRows();
        await refetchLayouts();
        handleRowClick(created);
      },
    };
    State.getState().pushToCommandStack({
      commandDefinitionId: "create-ReportObject",
      context,
    });
  }, [
    activeEntityIdentifiers,
    entities,
    handleRowClick,
    refetchLayouts,
    refetchRows,
  ]);

  // The report open is offered to the chat as the page's - its figures told
  // to the chat as its reader sees them.
  const openLayout = isReportOpen
    ? layouts.find(
        (layout) =>
          layout.id === selectedViewId || layout.identifier === selectedViewId,
      )
    : undefined;
  usePageThreadReference(
    openLayout
      ? {
          id:
            openLayout.id ??
            identifierAndTypenameToId(openLayout.identifier, "ReportObject"),
          identifier: openLayout.identifier,
          name: openLayout.name,
          typename: "ReportObject",
        }
      : null,
  );

  if (isReportOpen) {
    return <ReportsViewCanvas />;
  }

  return (
    <DataView
      totalCount={totalCount}
      columns={columns}
      rows={rows}
      loadNext={loadNext}
      isLoading={loading}
      networkStatus={networkStatus}
      onCheckedRowsButtonsProps={[deleteNodeButtonProps]}
      allowedViewModes={["card"]}
      viewMode="card"
      onRowClick={handleRowClick}
      onAddNew={handleAddNew}
      placeholderRow={Objects.ReportObject.placeholder}
      emptyState={{
        ReactIcon: Objects.ReportObject.ReactIcon,
        title: "No reports yet",
        description:
          "A report is a canvas of widgets built from your ledger — balances, spending, anything you can ask for.",
        actionLabel: "New report",
      }}
    />
  );
}

export function ReportsView() {
  return (
    <ReportsViewReportLayoutScope>
      <ReportsViewContent />
    </ReportsViewReportLayoutScope>
  );
}
